import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddEditDocumentModal from './AddEditDocumentModal'
import ViewDocumentModal from './ViewDocumentModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon, InformationIcon } from '../../../icons'
import { documentUploadDeletePermission, documentUploadWritePermission } from '../../../utils/permissionValidation'
import {
  loadDocumentsUpload,
  loadDocumentModels,
  loadDocumentTypes,
  loadDocumentCategories,
  loadAdminDocumentModels,
  removeDocument,
  clearDocumentsMessages,
} from '../../../actions/documents'
import { loadAccessLevels, loadLanguages, loadRegions } from '../../../actions/shared'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { formatSnakeCase, buildSnakeLabels } from '../../../utils/filters/filters'
import { documentUploadInstructions } from '../../../utils/fixedUrls'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'title',
    order: 'asc',
    translationKey: 'title_asc',
  },
  {
    field: 'title',
    order: 'desc',
    translationKey: 'title_desc',
  },
  {
    field: 'documentType',
    order: 'asc',
    translationKey: 'doc_type_asc',
  },
  {
    field: 'documentType',
    order: 'desc',
    translationKey: 'doc_type_desc',
  },
  {
    field: 'documentCategory',
    order: 'asc',
    translationKey: 'category_asc',
  },
  {
    field: 'documentCategory',
    order: 'desc',
    translationKey: 'category_desc',
  },
  {
    field: 'accessLevel',
    order: 'asc',
    translationKey: 'access_level_asc',
  },
  {
    field: 'accessLevel',
    order: 'desc',
    translationKey: 'access_level_desc',
  },
]

const Documents = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [documentsTableData, setDocumentsTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterType, setSelectedFilterType] = useState(null)
  const [selectedFilterLanguage, setSelectedFilterLanguage] = useState(null)
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null)
  const [selectedFilterAccessLevel, setSelectedFilterAccessLevel] = useState(null)
  const [statusesFilter, setStatusesFilter] = useState([])
  const [languages, setLanguages] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedDocument, setExpandedDocument] = useState(0)
  const [showAddEditDocumentModal, setShowAddEditDocumentModal] = useState(false)
  const [showViewDocumentModal, setShowViewDocumentModal] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [showDeleteDocumentConfirmModal, setShowDeleteDocumentConfirmModal] = useState(false)
  const [documentIdToDelete, setDocumentIdToDelete] = useState(null)
  const documentStatuses = ['active', 'inactive']
  const categoryLabels = buildSnakeLabels(props.documentCategories, (t) => t.value)
  const typeLabels = buildSnakeLabels(props.documentTypes, (t) => t.name)

  useEffect(() => {
    if (props.documentTypes.length === 0) {
      props.actions.loadDocumentTypes()
    }

    if (props.documentLanguages.length === 0) {
      props.actions.loadLanguages()
    }

    if (props.accessLevels.length === 0) {
      props.actions.loadAccessLevels()
    }

    if (props.documentCategories.length === 0) {
      props.actions.loadDocumentCategories()
    }

    if (props.regions.length === 0) {
      props.actions.loadRegions()
    }

    if (props.adminDocumentModels.length === 0) {
      props.actions.loadAdminDocumentModels()
    }
  }, [props.actions])

  useEffect(() => {
    const sortedLanguageCodes = props.documentLanguages
      .map((lang) => ({
        code: `${lang}-lang`,
        name: t([`filter_context.${lang}-lang`, lang]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => item.code)

    setLanguages(sortedLanguageCodes)
  }, [props.documentLanguages])

  useEffect(() => {
    const getUniqueDocumentLanguages = (files) => {
      if (files.length === 0) {
        return ''
      }

      let uniqueLanguages = new Set()

      files.forEach((file) => {
        if (file.multiLanguageCodes?.length > 0) {
          file.multiLanguageCodes.forEach((lang) => uniqueLanguages.add(lang))
        } else {
          uniqueLanguages.add(file.languageCode)
        }
      })

      return Array.from(uniqueLanguages)
        .sort((a, b) => a.localeCompare(b))
        .join(', ')
    }

    const getUniqueDocumentRegions = (files) => {
      if (files.length === 0) {
        return ''
      }

      let uniqueRegions = new Set()

      files.forEach((file) => {
        if (file.regions?.length > 0) {
          file.regions.forEach((region) => uniqueRegions.add(region.name))
        }
      })

      return Array.from(uniqueRegions)
        .sort((a, b) => a.localeCompare(b))
        .join(', ')
    }

    const tableData = props.documents.map((document, index) => {
      return {
        index,
        identifier: document.id,
        title: document.title,
        language: getUniqueDocumentLanguages((document.isExternalDocument ? document.externalDocuments : document.files) || []),
        type: document.documentType,
        category: document.documentCategory,
        region: getUniqueDocumentRegions(document.files || []),
        accessLevel: document.accessLevel,
        status: (document.isExternalDocument ? document.externalDocuments : document.files)?.some((f) => f.isActive)
          ? documentStatuses[0]
          : documentStatuses[1],
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditDocument(document.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditDocument(document.id, true)
            },
            requiredPermission: documentUploadWritePermission,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(document.id)
            },
            requiredPermission: documentUploadDeletePermission,
            isDisabled: (document.isExternalDocument ? document.externalDocuments : document.files)?.some((f) => f.isActive),
          },
        ],
      }
    })

    setDocumentsTableData(tableData)

    if (selectedDocument) {
      const updatedDocument = props.documents.find((document) => document.id === selectedDocument.id)

      if (updatedDocument) {
        setSelectedDocument(updatedDocument)
      }
    }
  }, [props.documents])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearDocumentsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearDocumentsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadDocuments(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadDocuments(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterModel, selectedFilterType, selectedFilterLanguage, selectedFilterCategory, selectedFilterAccessLevel, statusesFilter, orderBy])

  const handleLoadDocuments = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadDocumentsUpload(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterType(null)
    setSelectedFilterLanguage(null)
    setSelectedFilterCategory(null)
    setSelectedFilterAccessLevel(null)
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterType) {
      queryParams = { ...queryParams, documentType: selectedFilterType.name }
    }

    if (selectedFilterLanguage) {
      queryParams = { ...queryParams, language: selectedFilterLanguage }
    }

    if (selectedFilterCategory) {
      queryParams = { ...queryParams, documentCategory: selectedFilterCategory.value }
    }

    if (selectedFilterAccessLevel) {
      queryParams = { ...queryParams, accessLevel: selectedFilterAccessLevel }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === documentStatuses[0] }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const renderDocumentStatus = (status) => {
    switch (status) {
      case documentStatuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case documentStatuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const handleOpenViewEditDocument = (documentId, isEditModal) => {
    const document = props.documents.find((document) => document.id === documentId)

    if (document) {
      isEditModal ? setShowAddEditDocumentModal(true) : setShowViewDocumentModal(true)
      setSelectedDocument(document)
    }
  }

  const showConfirmationModal = (id) => {
    setDocumentIdToDelete(id)
    setShowDeleteDocumentConfirmModal(true)
  }

  const handleDeleteDocument = () => {
    setShowDeleteDocumentConfirmModal(false)
    props.actions.removeDocument(documentIdToDelete)
  }

  const closeModal = () => {
    setShowAddEditDocumentModal(false)
    setShowViewDocumentModal(false)
    setSelectedDocument(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={
              selectedFilterModel ||
              selectedFilterType ||
              selectedFilterLanguage ||
              selectedFilterCategory ||
              selectedFilterAccessLevel ||
              statusesFilter.length > 0
            }
            counterBadge={
              (selectedFilterModel && 1) +
              (selectedFilterType && 1) +
              (selectedFilterLanguage && 1) +
              (selectedFilterCategory && 1) +
              (selectedFilterAccessLevel && 1) +
              statusesFilter.length
            }
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditDocumentModal(true)} requiredPermission={documentUploadWritePermission}>
            {t('document_context.add_new_document')}
          </SimpleButton>
        </div>
        <div className="information-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="information-wrapper">
            <div className="title">
              {`${t('document_context.upload_instructions')} `}
              <span>
                <a href={documentUploadInstructions} target="_blank" rel="noreferrer" className="external-link">
                  {t('document_context.click_here')}
                </a>
              </span>
            </div>
          </div>
        </div>
        {isMobile && documentsTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && documentsTableData.length > 0 && (
          <GenericTable
            data={documentsTableData}
            headers={[
              {
                title: t('title'),
                orderKey: 'title',
              },
              {
                title: t('language'),
              },
              {
                title: t('document_context.doc_type'),
                orderKey: 'documentType',
              },
              {
                title: t('category'),
                orderKey: 'documentCategory',
              },
              {
                title: t('region'),
              },
              {
                title: t('access_level'),
                orderKey: 'accessLevel',
              },
              {
                title: t('status'),
              },
              {
                title: '',
              },
            ]}
            keys={['title', 'language', 'type', 'category', 'region', 'accessLevel', 'status', 'action']}
            keyRenderer={{
              title: (item) => {
                return <div className="title-wrapper">{item.title}</div>
              },
              language: (item) => {
                return <div className="language-wrapper">{item.language}</div>
              },
              accessLevel: (item) => t(item.accessLevel),
              status: (item) => {
                return <div className="status-wrapper">{renderDocumentStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(document) => handleOpenViewEditDocument(document.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          documentsTableData.map((document, index) => (
            <div key={index} className="card" onClick={() => setExpandedDocument(index)}>
              <div className={'card-item' + (expandedDocument !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('title')}</div>
                <div className={'card-item-body' + (expandedDocument !== index ? ' align-center' : '')}>
                  <div>{document.title}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={document.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              {expandedDocument === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('language')}</div>
                    <div className="card-item-body">{document.language}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('document_context.doc_type')}</div>
                    <div className="card-item-body">{document.type}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('category')}</div>
                    <div className="card-item-body">{document.category}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('region')}</div>
                    <div className="card-item-body">{document.region}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('access_level')}</div>
                    <div className="card-item-body">{t(document.accessLevel)}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderDocumentStatus(document.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && documentsTableData.length === 0 && (
          <EmptyPageResults title={t('document_context.no_documents_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: props.adminDocumentModels.map((model) => model.modelName),
              input: selectedFilterModel,
              output: (filterModel) => {
                setSelectedFilterModel(filterModel)
              },
            },
            {
              name: t('language'),
              type: FilterType.singleSelect,
              dataset: languages,
              input: selectedFilterLanguage ? `${selectedFilterLanguage}-lang` : null,
              output: (filterLanguage) => {
                const language = filterLanguage?.split('-')[0] || null
                setSelectedFilterLanguage(language)
              },
            },
            {
              name: t('type'),
              type: FilterType.singleSelect,
              dataset: typeLabels,
              input: selectedFilterType ? formatSnakeCase(selectedFilterType.name) : null,
              output: (filterType) => {
                const matched = props.documentTypes.find((c) => formatSnakeCase(c.name) === filterType)
                setSelectedFilterType(matched || null)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedFilterCategory ? formatSnakeCase(selectedFilterCategory.value) : null,
              output: (newCategory) => {
                const matched = props.documentCategories.find((c) => formatSnakeCase(c.value) === newCategory)
                setSelectedFilterCategory(matched || null)
              },
            },
            {
              name: t('access_level'),
              type: FilterType.singleSelect,
              dataset: props.accessLevels.map((level) => level.name),
              input: selectedFilterAccessLevel,
              output: (filterAccessLevel) => {
                setSelectedFilterAccessLevel(filterAccessLevel)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: documentStatuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditDocumentModal && (
        <AddEditDocumentModal selectedDocument={selectedDocument} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}
      {showViewDocumentModal && (
        <ViewDocumentModal
          selectedDocument={selectedDocument}
          handleEditDocument={() => {
            setShowViewDocumentModal(false)
            setShowAddEditDocumentModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showDeleteDocumentConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteDocumentConfirmModal(false)}
          onAccept={() => handleDeleteDocument()}
          onCancel={() => setShowDeleteDocumentConfirmModal(false)}
          message={t('document_context.confirm_delete_document')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={documentUploadDeletePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ documents, shared }) {
  return {
    documents: documents?.documentsUpload || [],
    documentTypes: documents?.documentTypes || [],
    documentLanguages: shared?.languages?.map((l) => l.languageCode) || [],
    accessLevels: shared?.accessLevels || [],
    documentCategories: documents?.documentCategories || [],
    regions: shared?.regions || [],
    adminDocumentModels: documents?.adminDocumentModels || [],
    isLoading: documents?.isLoading,
    canLoadMore: documents?.canLoadMoreDocumentsUpload,
    successMessage: documents?.successMessage,
    errorMessage: documents?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentsUpload,
        loadDocumentModels,
        loadDocumentTypes,
        loadDocumentCategories,
        loadAdminDocumentModels,
        clearDocumentsMessages,
        loadAccessLevels,
        loadRegions,
        loadLanguages,
        removeDocument,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Documents)

